import {
  Typenames,
  VlocNotEligibleToQualify,
  VlocPageId,
  VlocStartApplication,
} from '@varo/graphql-schema-types';
import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useGetEligibleNonEligibleDetails from 'features/lineOfCredit/screens/eligibility/hooks/useGetEligibleNonEligibleDetails';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import EligibilityScreenBody from './EligibilityScreenBody';

type TEligibilityProps = {
  data?: VlocStartApplication | VlocNotEligibleToQualify;
  serializedData?: undefined;
};

const EligibilityScreen = ({ data }: TEligibilityProps) => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('vloc');
  const [eligibilityStatus, setEligibilityStatus] = useState('');

  const { educationalScreenId: routeEducationalScreenId } =
    useLocalSearchParams<{
      educationalScreenId?: VlocPageId;
    }>();

  const educationalScreenId =
    routeEducationalScreenId ?? data?.educationalScreenId;

  const {
    isFetching,
    error,
    serializedData: educationalData,
  } = useGetEligibleNonEligibleDetails(educationalScreenId ?? '');

  const {
    body = [],
    disclaimer,
    header = [],
    footer = [],
  } = educationalData ?? {};

  const backgroundImage =
    header?.reduce((prevValue, item) => {
      if (item.__typename === Typenames.ImageHeaderComponentV2 && !prevValue) {
        return item.image;
      }
      return prevValue;
    }, undefined) || undefined;

  const headerText =
    header?.reduce((prevValue, item) => {
      if (item.__typename === Typenames.TextComponent && !prevValue) {
        return item.copy;
      }
      return prevValue;
    }, undefined) || undefined;

  const primaryButtonText = (() => {
    const buttonItem = footer?.find(
      (item) => item.__typename === Typenames.ButtonComponentV2,
    );
    return buttonItem?.buttonText || null;
  })();

  const vlocNotEligiblePressEvent = usePressableAnalytics({
    analyticsId: 'vlocNotEligibleClicked',
    customAttributes: {
      cta: 'See eligibility status',
      vloc_eligibility: 'false',
      vloc_preapproved: 'false',
    },
  });

  const vlocEligiblePressEvent = usePressableAnalytics({
    analyticsId: 'vlocEligibleClicked',
    customAttributes: {
      cta: 'Check for an offer',
      vloc_eligibility: 'true',
      vloc_preapproved: 'true',
    },
  });

  const buttonOnClick = () => {
    footer?.forEach((item) => {
      if (item.__typename === Typenames.ButtonComponentV2) {
        const { buttonOnClick } = item;

        if (buttonOnClick.__typename === Typenames.SDUserInformationAction) {
          vlocEligiblePressEvent();
          router.navigate({
            pathname: '/varo-line-of-credit/user-info',
          });
        } else if (
          buttonOnClick.__typename === Typenames.VlocPageNavigationAction
        ) {
          vlocNotEligiblePressEvent();
          router.navigate({
            pathname: '/varo-line-of-credit/not-eligible',
            params: { vlocPageId: buttonOnClick.vlocPageID },
          });
        }
      }
    });
  };

  const retrieveEligibilityOrApprovalStatus = (): boolean => {
    if (!footer?.length) {
      return false;
    }

    return footer.some(
      (item) =>
        item.__typename === Typenames.ButtonComponentV2 &&
        item.buttonOnClick?.__typename === Typenames.SDUserInformationAction,
    );
  };

  useEffect(() => {
    if (footer.length > 0) {
      const eligibility = String(retrieveEligibilityOrApprovalStatus());
      setEligibilityStatus(eligibility);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footer]);

  useViewDidFocusScreenAnalyticsEvent(
    eligibilityStatus
      ? {
          key:
            educationalScreenId === VlocPageId.VlocEduScreenInformation
              ? 'vlocEducational'
              : 'vlocOffer',
          customAttributes:
            educationalScreenId === VlocPageId.VlocEduScreenInformation
              ? undefined
              : {
                  vloc_eligibility: eligibilityStatus,
                  vloc_preapproved: eligibilityStatus,
                },
        }
      : null,
  );

  const handleBackArrowNavigation = () => {
    router.back();
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          title: translatedText('navigationTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              transparent={true}
              primaryOnPress={handleBackArrowNavigation}
            />
          ),
        }}
      />
      <ProductIntroScreen
        headlineText={headerText}
        headlineTextStyle="expressive"
        primaryButton={
          primaryButtonText
            ? {
                label: primaryButtonText,
                onPress: buttonOnClick,
              }
            : undefined
        }
        bodyContent={
          <EligibilityScreenBody body={body} disclaimer={disclaimer} />
        }
        backgroundTexture={backgroundTextureLight as ImageSourcePropType}
        visual={
          backgroundImage?.illustrationKey as unknown as IllustrationKeyType
        }
        topPadding={80}
      />

      <ScreenTransitionOverlay
        isLoading={isFetching}
        LoadingComponent={
          <View style={{ paddingTop: insets.top }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-eligibilityScreen"
              trackingAnalyticsId="eligibility-error-tracking"
              buttonPressKey="eligibility-errorButtonPress"
              trackingCustomAttributes={{ step_name: VlocStepName.VLOC_OFFER }}
              insetBottomSafeArea={true}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default EligibilityScreen;
